import React, { memo, forwardRef } from 'react';
import PropTypes from 'prop-types';
import './styles.scss';

const InfoCard = memo(forwardRef(({
  title,
  icon,
  children,
  actions,
  variant = 'primary',
  size = 'medium',
  loading = false,
  error = null,
  className = '',
  headerClassName = '',
  bodyClassName = '',
  onClick,
  'aria-label': ariaLabel,
  'data-testid': testId
}, ref) => {
  const cardClasses = [
    'info-card-container',
    `info-card--${variant}`,
    `info-card--${size}`,
    loading && 'info-card--loading',
    error && 'info-card--error',
    onClick && 'info-card--clickable',
    className
  ].filter(Boolean).join(' ');

  const headerClasses = [
    'info-card-header',
    'd-flex',
    'justify-content-between',
    'align-items-center',
    headerClassName
  ].filter(Boolean).join(' ');

  const bodyClasses = [
    'card-body',
    bodyClassName
  ].filter(Boolean).join(' ');

  const handleClick = (e) => {
    if (onClick) {
      onClick(e);
    }
  };

  return (
    <div
      ref={ref}
      className={cardClasses}
      onClick={handleClick}
      role={onClick ? 'button' : 'region'}
      tabIndex={onClick ? 0 : undefined}
      aria-label={ariaLabel || title}
      aria-busy={loading}
      data-testid={testId}
    >
      <div className={headerClasses}>
        <div className="d-flex align-items-center gap-2">
          {icon && (
            <i 
              className={`bi bi-${icon} info-card-icon`} 
              aria-hidden="true"
            />
          )}
          <h2 className="info-card-title">{title}</h2>
        </div>
        <div className="info-card-actions">
          {actions}
        </div>
      </div>
      
      <div className={bodyClasses}>
        {loading && (
          <div className="info-card-loading">
            <div className="info-card-spinner" aria-hidden="true" />
            <span className="sr-only">Cargando...</span>
          </div>
        )}
        
        {error && (
          <div className="info-card-error" role="alert">
            <i className="bi bi-exclamation-triangle" aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}
        
        {!loading && !error && children}
      </div>
    </div>
  );
}));

InfoCard.displayName = 'InfoCard';

InfoCard.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.string,
  children: PropTypes.node,
  actions: PropTypes.node,
  variant: PropTypes.oneOf(['primary', 'secondary', 'success', 'warning', 'error']),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  loading: PropTypes.bool,
  error: PropTypes.string,
  className: PropTypes.string,
  headerClassName: PropTypes.string,
  bodyClassName: PropTypes.string,
  onClick: PropTypes.func,
  'aria-label': PropTypes.string,
  'data-testid': PropTypes.string
};

export default InfoCard;
